import { useState, useEffect, useRef } from 'react'
import { Link, useRouter } from '../lib/router'

const NotFoundPage = () => {
  const { navigate } = useRouter()
  const [countdown, setCountdown] = useState(10)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) return 0
        return prev - 1
      })
    }, 1000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(() => {
    if (countdown > 0) return
    if (intervalRef.current) clearInterval(intervalRef.current)
    const id = requestAnimationFrame(() => navigate('/'))
    return () => cancelAnimationFrame(id)
  }, [countdown, navigate])

  const handleHomeClick = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
  }

  const sectionStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    textAlign: 'center',
  }

  const errorCodeStyle: React.CSSProperties = {
    fontSize: 'clamp(6rem, 20vw, 12rem)',
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: '-4px',
    color: 'var(--accent-color)',
    textShadow: 'var(--glow)',
    marginBottom: '0.5rem',
  }

  const countdownTextStyle: React.CSSProperties = {
    color: 'var(--text-secondary)',
    fontSize: '0.9rem',
    opacity: 0.7,
    marginBottom: '0.75rem',
  }

  const countdownNumStyle: React.CSSProperties = {
    display: 'inline-block',
    color: 'var(--accent-color)',
    fontWeight: 700,
    fontSize: '1.1rem',
    minWidth: '1.8ch',
    animation: countdown <= 3 ? 'pulse 0.6s ease-in-out infinite alternate' : 'none',
  }

  const progressTrackStyle: React.CSSProperties = {
    width: 200,
    height: 3,
    borderRadius: 2,
    background: 'var(--border-color)',
    overflow: 'hidden',
    margin: '0 auto 2.5rem',
  }

  const progressFillStyle: React.CSSProperties = {
    height: '100%',
    borderRadius: 2,
    background: 'var(--accent-color)',
    animation: 'countdown-bar 10s linear forwards',
  }

  return (
    <section style={sectionStyle}>
      <div style={errorCodeStyle}>404</div>
      <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 700, marginBottom: '1rem' }}>
        Page Not Found
      </h1>
      <p style={{
        color: 'var(--text-secondary)',
        fontSize: '1.1rem',
        lineHeight: 1.6,
        marginBottom: '1.5rem',
        maxWidth: 500,
      }}>
        The page you're looking for doesn't exist or was moved.
        Let me help you find your way back.
      </p>
      <p style={countdownTextStyle}>
        Redirecting to home in{' '}
        <span style={countdownNumStyle}>{countdown}</span>
        {' '}second{countdown !== 1 ? 's' : ''}
      </p>
      <div style={progressTrackStyle}>
        <div style={progressFillStyle} />
      </div>
      <Link
        href="/#top"
        onClick={handleHomeClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.85rem 2rem',
          borderRadius: 8,
          background: 'transparent',
          color: 'var(--accent-color)',
          border: '1px solid var(--border-color)',
          fontSize: '1rem',
          fontWeight: 600,
          cursor: 'pointer',
          textDecoration: 'none',
          transition: 'all 0.3s',
        }}
      >
        Back to Home
      </Link>
    </section>
  )
}

export default NotFoundPage
